// COP — Living-Net resting READ grammar (0.1b CO-HERO layer).
//
// DESIGN-SPEC §2 (the ratified layer-priority stack, CO-HERO #2) + §5 build-step 5.
// This is the priority-1 SOCIAL read on the resting glass — the only layer that
// protagonizes the charter's named #1 (Waze-style social) as a *resting* grammar.
// It ships READ-ONLY; the WRITE path (contribution loop) is milestone-deferred to
// 0.3b (DESIGN-SPEC §1, §2 SLOT-ONLY).
//
// It has three parts, each routed to ONE perceptually-separable channel under the
// CHANNEL-ASSIGNMENT LAW (DESIGN-CONTEXT §2.2). The law is structural, not
// advisory: a costume that collides with an owned channel is a FAIL. The owned
// channels are:
//     affiliation        = frame SHAPE              (milsym, symbology.ts)
//     staleness/freshness = peak-alpha OPACITY       (the smear)
//     confidence/trust    = cloud REACH + EDGE-sigma (the smear)
//     pending-affiliation = LINE-DASH                (the pending quatrefoil frame)
//     selection           = additive HALO + leader
//     urgency             = MOTION (reserved)
//
// ── THE THREE PARTS AND THEIR CHANNELS ──────────────────────────────────────
//
//  1. PRESENCE (own-side units reachable now) → peak-alpha OPACITY (freshness),
//     DISAMBIGUATED from ordinary sensor-staleness by FRAME SHAPE (presence is
//     ALWAYS a friendly frame). A presence node carries a fresh `presence_seen`
//     heartbeat that drives a SHORTER staleness window, so its smear alpha pulses
//     as a reachability heartbeat. Same opacity channel as staleness — the friendly
//     frame is what tells the operator "this dim is a heartbeat, not a stale sensor
//     track" (the gold-pack open risk: presence-dimming on a friendly frame must
//     read distinctly from sensor-staleness-dimming on a non-friendly frame).
//
//  2. CROWD-vs-AUTHORITATIVE trust tier → crowd wears a falsifiable INNER-PIP
//     COSTUME (a small hollow corner ring drawn INSIDE the milsym sprite). This is
//     a NON-HUE, NON-OPACITY, NON-DASH, NON-SHAPE channel — it does not touch the
//     frame geometry (affiliation), the dimension modifier (domain), the centred
//     glyph (domain), the dash (pending), or the alpha (staleness). It is the
//     OpenCTI "solid-vs-dotted + magic-wand" pattern for derived/low-pedigree
//     content (DESIGN-CONTEXT §4.4, §4.6): the crowd mark must be visually QUIETER
//     and more falsifiable than ground truth, and survive a hue strip (it is a
//     stroke, hue-independent). Authoritative wears NO pip — it is the dominant,
//     unmarked, solid tier. Crowd NEVER overwrites authoritative.
//
//  3. CONFIRMATION-DECAY → the CONFIDENCE channel (smear reach + edge-sigma).
//     Crowd facts are born at a lower confidence than authoritative ones, and lose
//     confidence over time unless downstream-confirmed (Waze auto-expiring stale
//     reports / Google "is this still here?"). Lower confidence => a WIDER, SOFTER
//     smear (the existing reach+sigma derivation in deckBackend.smearGeometry reads
//     `properties.confidence` directly). Authoritative = higher confidence => a
//     tighter, crisper smear. This rides the confidence channel ONLY — it never
//     touches alpha (staleness) and never touches the frame (affiliation).
//
// The data is shaped on the REAL hormuz envelopes — no contract change. Everything
// rides the open `properties` bag (trust_tier, presence, crowd_pip, confidence,
// presence_seen). Markings/affiliation are untouched so the PDP filter (I4) still
// bites unchanged.

import type { CanonicalEnvelope } from "../../../../packages/schema/ts/envelope.ts";

/** The trust tier a fact is rendered in. Authoritative = sensor/intel ground
 *  truth (solid, dominant, unmarked). Crowd = OSINT-class, falsifiable, born at
 *  lower confidence and decaying unless confirmed (quieter, inner-pip costume). */
export type TrustTier = "authoritative" | "crowd";

/** The Living-Net read-state classification for one record. Derived once at build
 *  time from the corpus (coherent, deterministic — never random per frame). */
export interface LivingNetTag {
  /** Crowd vs authoritative. Drives the inner-pip costume + the confidence floor. */
  trustTier: TrustTier;
  /** True for own-side units that are "reachable now" (blue-force presence). Drives
   *  the presence heartbeat (a shorter, fresher staleness window). */
  presence: boolean;
}

// ── Trust-tier derivation (coherent, from the corpus) ────────────────────────
//
// AUTHORITATIVE: anything with a real INT-discipline pedigree (SIGINT/GEOINT/
// HUMINT/IMINT/imagery/ELINT site/node/summary/report) or a sensed military
// platform (combatants, fast-attack craft, patrol boats, MPA/fighters, radar/
// sensor sites). These are the dominant, solid ground-truth tier.
//
// CROWD (OSINT-class, falsifiable): low-pedigree civilian-observed or
// unverified-assessment contacts — fishing dhows (community-reported), the
// "assessed"/"historical"/"re-acquired"/"unidentified" unverified observations,
// and the unsafe-intercept incident report. These wear the falsifiable costume.
//
// The friendly OWN force is ALWAYS authoritative (own sensors) — a friendly frame
// never wears the crowd costume, which keeps part-1 (presence) and part-2 (crowd)
// visually orthogonal: presence ⊂ friendly ⊂ authoritative.

const AUTHORITATIVE_PEDIGREE =
  /sigint|geoint|humint|imint|imagery|elint|masint|radar|sensor|site|node|summary|report|combatant|destroyer|frigate|ddg|cruiser|corvette|patrol|poseidon|fighter|interceptor|fast-(attack|inshore)|fac\b|fiac|swarm|carrier-strike|aegis|cutter/i;

const CROWD_SIGNAL =
  /\bdhow\b|fishing|assessed|historical|re-acquired|reacquired|unidentified|unidentif|rumou?r|community|civil-report|stringer|social|open-source|osint/i;

/** The minimal record shape both the corpus tagger (HormuzRecord) and the
 *  envelope reader can satisfy — pedigree lives in name + type only (NEVER in the
 *  markings, so trust-tier classification can never leak a classification). */
export interface PedigreeRecord {
  affiliation?: string | null;
  kind?: string | null;
  name?: string | null;
  type?: string | null;
}

/** Classify one record's trust tier from its corpus pedigree. Friendly own-force
 *  is always authoritative; an explicit INT pedigree is authoritative; otherwise a
 *  crowd signal (civilian-observed / unverified-assessment) marks it crowd; the
 *  default is authoritative (we never DOWNGRADE a contact to crowd without a signal
 *  — crowd is the marked, subordinate exception, authoritative is the floor). */
export function trustTierOfRecord(rec: PedigreeRecord): TrustTier {
  if (rec.affiliation === "friend") return "authoritative";
  const text = `${rec.name ?? ""} ${rec.type ?? ""}`;
  if (AUTHORITATIVE_PEDIGREE.test(text)) return "authoritative";
  if (CROWD_SIGNAL.test(text)) return "crowd";
  return "authoritative";
}

/** True for own-side friendly units that are "reachable now" (blue-force presence).
 *  Presence is a strict subset of friendly (own side) AND a live track/alert (a
 *  reachable mover, not a static asset/observation). A friendly static site is own
 *  force but not a "reachable-now presence node" in the heartbeat sense. */
export function isPresenceRecord(rec: PedigreeRecord): boolean {
  if (rec.affiliation !== "friend") return false;
  return rec.kind === "track" || rec.kind === "alert";
}

/** Read an envelope's pedigree fields into the minimal record shape. */
function recordOf(env: CanonicalEnvelope): PedigreeRecord {
  const p = env.properties as Record<string, unknown>;
  return {
    affiliation: typeof p.affiliation === "string" ? p.affiliation : null,
    kind: env.kind,
    name: typeof p.name === "string" ? p.name : null,
    type: typeof p.type === "string" ? p.type : null,
  };
}

/** Classify one envelope's trust tier (delegates to trustTierOfRecord). */
export function trustTierOf(env: CanonicalEnvelope): TrustTier {
  return trustTierOfRecord(recordOf(env));
}

/** True if the envelope is an own-side reachable-now presence node. */
export function isPresence(env: CanonicalEnvelope): boolean {
  return isPresenceRecord(recordOf(env));
}

/** The full Living-Net tag for one envelope. */
export function livingNetTag(env: CanonicalEnvelope): LivingNetTag {
  return { trustTier: trustTierOf(env), presence: isPresence(env) };
}

// ── The confidence floor + confirmation-decay (CONFIDENCE channel) ───────────
//
// Crowd facts are born LOWER-confidence than authoritative ones, and lose
// confidence over time UNLESS downstream-confirmed. This is applied at the DATA
// layer (scenarios/hormuz.ts) onto `properties.confidence`, which the existing
// smear derivation (deckBackend.smearGeometry) reads to set reach + sigma. Lower
// confidence => wider/softer smear (a quieter, more-falsifiable cloud); the
// authoritative tier stays tight/crisp. This rides the confidence channel ONLY.

/** Confidence ceiling for the crowd tier: a crowd fact never reads as crisp ground
 *  truth, so its smear is always at least somewhat soft. Authoritative facts are
 *  uncapped (they keep their high corpus confidence). */
export const CROWD_CONFIDENCE_CEILING = 0.6;

/** How much confidence a fully-decayed (long-unconfirmed) crowd fact loses,
 *  relative to its birth confidence. A crowd fact that is NOT downstream-confirmed
 *  drifts from its birth confidence down toward (birth * (1 - this)). Confirmed
 *  crowd facts hold near birth. Authoritative facts never decay on this axis. */
export const CROWD_DECAY_FRACTION = 0.45;

/** Confidence FLOOR a decayed crowd fact never falls below — it stays a faint,
 *  honest "fog to push through", never dissolving to a misleading zero-belief. */
export const CROWD_CONFIDENCE_FLOOR = 0.2;

/**
 * Apply the crowd confidence floor + confirmation-decay to a birth confidence.
 *
 * `confirmed` is the downstream-confirmation fraction in [0,1]: 1 = freshly
 * confirmed by a sensor/second report (holds near birth), 0 = long-unconfirmed
 * (fully decayed). Authoritative facts pass through UNCHANGED (they do not ride
 * this decay axis — they are ground truth).
 *
 * The result feeds `properties.confidence` → the smear's reach + sigma. Lower =>
 * wider/softer cloud. This is the ONLY thing decay touches (never alpha, never
 * the frame): trust/confirmation is the confidence channel, full stop.
 */
export function decayedConfidence(
  tier: TrustTier,
  birthConfidence: number,
  confirmed: number,
): number {
  if (tier === "authoritative") return birthConfidence;
  const conf = clamp01(confirmed);
  // Cap the crowd birth confidence so a crowd fact is never as crisp as ground truth.
  const capped = Math.min(birthConfidence, CROWD_CONFIDENCE_CEILING);
  // Decay toward (capped * (1 - CROWD_DECAY_FRACTION)) as confirmation -> 0.
  const decayed = capped * (1 - CROWD_DECAY_FRACTION * (1 - conf));
  return Math.max(CROWD_CONFIDENCE_FLOOR, +decayed.toFixed(3));
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

// ── The CROWD inner-pip COSTUME sprite (CHANNEL: geometry, not hue/opacity/dash) ──
//
// The crowd costume must ride a NON-HUE, NON-OPACITY, NON-DASH, NON-FRAME-SHAPE
// channel (DESIGN-CONTEXT §2.2): hue confirms affiliation, opacity owns staleness,
// dash owns pending-affiliation, frame shape owns affiliation, the centred glyph
// owns domain. So the crowd mark is an INNER PIP — a small HOLLOW ACHROMATIC RING
// (the OpenCTI "solid-vs-dotted / magic-wand" derived-content idiom, §4.4/§4.6),
// rendered as a corner tag OFFSET from the frame centre (so it never collides with
// the centred domain glyph or the above-frame dimension modifier). It is a stroke,
// so it is hue-independent and survives the MONO master test; it is quiet (small,
// thin) so crowd reads as SUBORDINATE to the unmarked authoritative tier.

/** A deck.gl-IconLayer-ready badge sprite (a transparent PNG data URL + dims). */
export interface PipSprite {
  url: string;
  width: number;
  height: number;
}

let pipSpriteCache: PipSprite | null = null;

/** 1×1 transparent PNG fallback for SSR / no-canvas environments. */
const TRANSPARENT_PIP: PipSprite = {
  url:
    "data:image/png;base64," +
    "iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==",
  width: 1,
  height: 1,
};

/**
 * The crowd inner-pip sprite: a small HOLLOW double-stroke ring (a thin outer
 * dark halo + a light inner ring) so it reads on BOTH the dark basemap and the
 * lit frame. Achromatic on purpose — its meaning is the GEOMETRY (a hollow ring =
 * "falsifiable, crowd-sourced"), not any colour. Rasterised once and cached.
 */
export function crowdPipSprite(): PipSprite {
  if (pipSpriteCache) return pipSpriteCache;
  if (typeof document === "undefined") return TRANSPARENT_PIP;

  const dpr =
    typeof window !== "undefined" && window.devicePixelRatio
      ? Math.max(1, Math.min(window.devicePixelRatio, 3))
      : 1;
  const css = 12; // logical sprite size (px)
  const px = Math.round(css * dpr);
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext("2d");
  if (!ctx) return TRANSPARENT_PIP;
  ctx.scale(dpr, dpr);

  const c = css / 2;
  const r = css / 2 - 2.5;

  // Outer dark halo so the ring stays legible over a light frame fill.
  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.lineWidth = 3;
  ctx.strokeStyle = "rgba(9,12,17,0.85)";
  ctx.stroke();

  // Inner light ring — the actual "falsifiable" hollow-pip mark (achromatic).
  ctx.beginPath();
  ctx.arc(c, c, r, 0, Math.PI * 2);
  ctx.lineWidth = 1.4;
  ctx.strokeStyle = "rgba(226,232,240,0.95)";
  ctx.stroke();

  const sprite: PipSprite = { url: canvas.toDataURL("image/png"), width: px, height: px };
  pipSpriteCache = sprite;
  return sprite;
}

/** Clear the cached pip sprite (e.g. after a DPR / theme change). */
export function clearCrowdPipCache(): void {
  pipSpriteCache = null;
}
